import type {
  ClientWsCloseDiagnostics,
  ClientWsErrorDiagnostics,
  WsStatus,
  WsTransportProbeState,
} from "@/hooks/thread-sync-client-types"

export type ThreadSyncTransportState = {
  ws: WebSocket | null
  connectionVersion: number
  helloSent: boolean
  deferInitialHandshake: boolean
  connectEnabled: boolean
  viewerActivityEnabled: boolean
  readOnlyTransport: boolean
  hasConnected: boolean
  firstConnectionAttemptStartedAt: number | null
  reconnectAttempts: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  connectingWatchdogTimer: ReturnType<typeof setTimeout> | null
  keepaliveTimer: ReturnType<typeof setInterval> | null
  keepaliveFrameType: "sync.ping" | "sync.activity"
  localCloseReason: string | null
  status: WsStatus
  wsStatusChangedAt: number
  wsDegradedSinceAt: number | null
  wsTransportProbeState: WsTransportProbeState
  wsTransportProbeAt: number | null
  lastWsCloseDiagnostics: ClientWsCloseDiagnostics | null
  lastWsErrorDiagnostics: ClientWsErrorDiagnostics | null
}

export function createThreadSyncTransportState(args?: {
  connectEnabled?: boolean
  viewerActivityEnabled?: boolean
  readOnlyTransport?: boolean
  nowMs?: number
}): ThreadSyncTransportState {
  const nowMs = args?.nowMs ?? Date.now()
  const viewerActivityEnabled = args?.viewerActivityEnabled === true

  return {
    ws: null,
    connectionVersion: 0,
    helloSent: false,
    deferInitialHandshake: false,
    connectEnabled: args?.connectEnabled !== false,
    viewerActivityEnabled,
    readOnlyTransport: args?.readOnlyTransport === true,
    hasConnected: false,
    firstConnectionAttemptStartedAt: null,
    reconnectAttempts: 0,
    reconnectTimer: null,
    connectingWatchdogTimer: null,
    keepaliveTimer: null,
    keepaliveFrameType: viewerActivityEnabled ? "sync.activity" : "sync.ping",
    localCloseReason: null,
    status: "disconnected",
    wsStatusChangedAt: nowMs,
    wsDegradedSinceAt: null,
    wsTransportProbeState: "unknown",
    wsTransportProbeAt: null,
    lastWsCloseDiagnostics: null,
    lastWsErrorDiagnostics: null,
  }
}

export function applyTransportOptions(
  transport: ThreadSyncTransportState,
  args?: {
    connectEnabled?: boolean
    viewerActivityEnabled?: boolean
    readOnlyTransport?: boolean
  },
): void {
  transport.connectEnabled = args?.connectEnabled !== false
  transport.viewerActivityEnabled = args?.viewerActivityEnabled === true
  transport.readOnlyTransport = args?.readOnlyTransport === true
  transport.keepaliveFrameType = transport.viewerActivityEnabled ? "sync.activity" : "sync.ping"
}

export function hasOpenOrConnectingTransportSocket(transport: ThreadSyncTransportState): boolean {
  return !!transport.ws &&
    (transport.ws.readyState === WebSocket.OPEN || transport.ws.readyState === WebSocket.CONNECTING)
}

export function clearReconnectTimer(transport: ThreadSyncTransportState): void {
  if (transport.reconnectTimer) {
    clearTimeout(transport.reconnectTimer)
    transport.reconnectTimer = null
  }
}

export function clearConnectingWatchdogTimer(transport: ThreadSyncTransportState): void {
  if (transport.connectingWatchdogTimer) {
    clearTimeout(transport.connectingWatchdogTimer)
    transport.connectingWatchdogTimer = null
  }
}

export function clearKeepaliveTimer(transport: ThreadSyncTransportState): void {
  if (transport.keepaliveTimer) {
    clearInterval(transport.keepaliveTimer)
    transport.keepaliveTimer = null
  }
}

export function clearTransportTimers(transport: ThreadSyncTransportState): void {
  clearReconnectTimer(transport)
  clearConnectingWatchdogTimer(transport)
  clearKeepaliveTimer(transport)
}

export function closeTransportSocket(transport: ThreadSyncTransportState, reason: string): void {
  if (!transport.ws) {
    transport.localCloseReason = reason
    return
  }

  const ws = transport.ws
  transport.ws = null
  transport.localCloseReason = reason
  ws.onopen = null
  ws.onmessage = null
  ws.onerror = null
  ws.onclose = null
  try {
    ws.close(1000, reason)
  } catch {
    // ignore
  }
}

export function beginTransportConnectAttempt(
  transport: ThreadSyncTransportState,
  args?: { nowMs?: number },
): number {
  clearReconnectTimer(transport)
  clearConnectingWatchdogTimer(transport)
  clearKeepaliveTimer(transport)
  transport.connectionVersion += 1
  transport.helloSent = false
  transport.deferInitialHandshake = false
  transport.localCloseReason = null
  transport.ws = null
  if (!transport.hasConnected && transport.firstConnectionAttemptStartedAt === null) {
    transport.firstConnectionAttemptStartedAt = args?.nowMs ?? Date.now()
  }
  return transport.connectionVersion
}

export function beginTransportDisconnect(
  transport: ThreadSyncTransportState,
  args: { closeReason: string },
): void {
  clearTransportTimers(transport)
  transport.connectionVersion += 1
  transport.helloSent = false
  transport.deferInitialHandshake = false
  closeTransportSocket(transport, args.closeReason)
}

export function recordTransportError(args: {
  transport: ThreadSyncTransportState
  message: string
  nowMs: number
}): void {
  args.transport.lastWsErrorDiagnostics = {
    at: args.nowMs,
    message: args.message,
  }
}

export function recordTransportClose(args: {
  transport: ThreadSyncTransportState
  event: CloseEvent
  nowMs: number
}): void {
  args.transport.ws = null
  clearConnectingWatchdogTimer(args.transport)
  clearKeepaliveTimer(args.transport)
  args.transport.helloSent = false
  args.transport.deferInitialHandshake = false
  args.transport.lastWsCloseDiagnostics = {
    at: args.nowMs,
    code: args.event.code,
    reason: args.event.reason || null,
    localInitiatorReason: args.transport.localCloseReason,
    visibilityState: typeof document !== "undefined" ? document.visibilityState : "unknown",
    online:
      typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
        ? navigator.onLine
        : null,
  }
  args.transport.localCloseReason = null
}

export function setTransportStatus(args: {
  transport: ThreadSyncTransportState
  status: WsStatus
  nowMs: number
  suppressDegradedTracking?: boolean
}): void {
  if (args.transport.status !== args.status) {
    args.transport.status = args.status
    args.transport.wsStatusChangedAt = args.nowMs
  }

  if (args.status === "connected") {
    args.transport.hasConnected = true
    args.transport.firstConnectionAttemptStartedAt = null
    args.transport.wsDegradedSinceAt = null
  } else if (args.suppressDegradedTracking === true) {
    args.transport.wsDegradedSinceAt = null
  } else if (args.transport.hasConnected && args.transport.wsDegradedSinceAt === null) {
    args.transport.wsDegradedSinceAt = args.nowMs
  }
}

export function computeReconnectDelayMs(args: {
  reconnectAttempts: number
  maxDelayMs: number
}): number {
  return Math.min(1000 * 2 ** Math.max(0, args.reconnectAttempts), args.maxDelayMs)
}

export function incrementReconnectAttempts(transport: ThreadSyncTransportState): void {
  transport.reconnectAttempts += 1
}

export function isCurrentTransportConnection(
  transport: ThreadSyncTransportState,
  ws: WebSocket,
  version: number,
): boolean {
  return transport.ws === ws && transport.connectionVersion === version
}
